'use client';

import { useTranslation } from 'react-i18next';

import { Divider } from '@sola/ark-core/atoms/divider/divider.js';
import { AddressSelectSupporter } from '@sola/ark-core/molecules/address-select/address-select-supporter.js';
import { Fieldset } from '@sola/ark-core/molecules/fieldset/fieldset.js';
import {
  Form,
  FormProps,
  useFormMethods,
} from '@sola/ark-core/molecules/form/form.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import {
  GraphQLCommitment,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.js';
import { IconNames } from '@sola/schema/lib/icons/icon-names.js';

import {
  getIconName,
  getPaymentMethodLabel,
} from '../../atoms/payment-method-icon/payment-method-icon';
import { RecurringGivingOptions } from '../../organisms/recurring-giving-options/recurring-giving-options';
import { SectionContent } from '../section-content/section-content';
import { UpdateACHFields } from './update-ach-fields';
import { UpdateCCFields } from './update-cc-fields';

export type PaymentMethodUpdateFormProps = {
  paymentMethod: GraphQLSupporterPaymentMethod;
  nonRecurringCommitments?: GraphQLCommitment[] | null;
  children: React.ReactNode;
  onSubmit: FormProps['onSubmit'];
};

export const PaymentMethodUpdateForm: React.FC<
  PaymentMethodUpdateFormProps
> = ({ paymentMethod, nonRecurringCommitments, children, onSubmit }) => {
  const { t } = useTranslation('my');
  const formMethods = useFormMethods();

  return (
    <Form
      onSubmit={onSubmit}
      formMethods={formMethods}
      options={{ mode: 'onTouched' }}
      isCustomError
      noValidate
    >
      <div>
        {/* ACH Details */}
        {paymentMethod.ach && (
          <SectionContent heading={t('payments.methodForm.formTitle.bank')}>
            <div className="w-full flex flex-col gap-comfy-xl">
              <div className="flex flex-row gap-comfy-xs md:gap-comfy-md items-center">
                <ArkIcon
                  className="PaymentMethodUpdateSheet__icon"
                  iconName={getIconName(paymentMethod) as IconNames}
                />
                <span className="block label-md label-moderate md:label-lg mb-snug-md">
                  {getPaymentMethodLabel(paymentMethod, t)}
                </span>
              </div>
              <Fieldset id="ach-fields">
                <UpdateACHFields paymentMethod={paymentMethod} />
              </Fieldset>
            </div>
          </SectionContent>
        )}

        {/* Card Info */}
        {paymentMethod.creditCard && (
          <>
            <SectionContent heading={t('payments.methodForm.formTitle.card')}>
              <div className="w-full flex flex-col gap-comfy-xl">
                <div className="flex flex-row gap-comfy-xs md:gap-comfy-md items-center">
                  <ArkIcon
                    className="PaymentMethodUpdateSheet__icon"
                    iconName={getIconName(paymentMethod) as IconNames}
                  />
                  <span className="block label-md label-moderate md:label-lg mb-snug-md">
                    {getPaymentMethodLabel(paymentMethod, t)}
                  </span>
                </div>
                <Fieldset id="cc-fields">
                  <UpdateCCFields paymentMethod={paymentMethod} />
                </Fieldset>
              </div>
            </SectionContent>

            <Divider purpose="neutral" className="my-comfy-xl" />

            <SectionContent
              heading={t('payments.methodForm.formTitle.billingInfo')}
            >
              <AddressSelectSupporter
                label={t('payments.methodForm.fieldLabels.billingInfo')}
                paymentMethod={paymentMethod}
              />
            </SectionContent>
          </>
        )}

        <Divider purpose="neutral" className="my-comfy-xl" />

        {/* Recurring giving options */}
        {!!nonRecurringCommitments?.length && (
          <SectionContent
            heading={t('payments.methodForm.formTitle.recurringGiving')}
          >
            <RecurringGivingOptions commitments={nonRecurringCommitments} />
          </SectionContent>
        )}

        {/* Form submit */}
        {children}
      </div>
    </Form>
  );
};
